import styled from '@emotion/styled';
import { useSelector } from 'react-redux';
import P1 from '@base/P1/P1';
import P2 from '@base/P2/P2';
import H2 from '@base/H2/H2';
import H5 from '@base/H5/H5';
import Link from '@base/Link/Link';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import { categoryIdIconMap } from '@constants/helpCenter';
import { getIsSubscribed } from '@selectors/subscription';
import { getIsSubscribedToPlatformBatchGroup } from '@selectors/auth';

const Wrapper = styled.div`
  grid-column: 4 / 22;
  margin-top: ${Spacings.SPACING_2B};
  margin-bottom: ${Spacings.SPACING_16B};
`;

const MainTitle = styled(P1)`
  font-size: ${Fonts.H5};
`;

const SubTitle = styled(H2)`
  margin-top: ${Spacings.SPACING_2B};
  margin-bottom: ${Spacings.SPACING_10B};
`;

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-row-gap: ${Spacings.SPACING_6B};
  grid-column-gap: ${Spacings.SPACING_4B};

  ${mediaQuery.uptoTablet} {
    display: flex;
    flex-flow: column;
  }
`;

const Category = styled.div`
  border-radius: ${Spacings.SPACING_2B};
  box-shadow: ${boxShadowGenerator()};
  background-color: var(--color-base-1);
  cursor: pointer;
`;

const ChatCategory = styled(Category)`
  padding: ${Spacings.SPACING_6B};
  cursor: default;
`;

const Icon = styled.img`
  user-select: none;
  height: 40px;
  margin-bottom: ${Spacings.SPACING_4B};
`;

const CategoryDesc = styled(P2)`
  margin-top: ${Spacings.SPACING_2B};
`;

const BoldP1 = styled(P1)`
  font-weight: ${Fonts.BOLD};
  line-height: 100%;
  color: var(--color-i-green);
  border-bottom: 1px dashed var(--color-i-green);
  display: inline-block;
  cursor: pointer;
  margin-top: ${Spacings.SPACING_2B};
`;

const anchorStyle = `
  width: 100%;
  height: 100%;
  display: block;
  padding: ${Spacings.SPACING_6B};
`;

const Categories = () => {
  const categories = useSelector(
    ({ helpCenter }) => helpCenter.categories.results || []
  );
  const openChat = () => {
    if (!window.Freshbots) return;
    if (!window.Freshbots.freddyIsInitiatedChat) {
      window.Freshbots.initiateChat(true);
      window.Freshbots.freddyIsInitiatedChat = true;
    }
    window.Freshbots.showWidget(true);
  };
  const onCategoryClick = () => () => {};
  const isEducator = useSelector(({ auth }) => auth.me?.isEducator) || false;
  const isSubscribed = useSelector(getIsSubscribed);
  const isSubscribedToPlatformBatch = useSelector(
    getIsSubscribedToPlatformBatchGroup
  );

  const showFreshbot = isSubscribed || isSubscribedToPlatformBatch;
  return (
    <Wrapper>
      <MainTitle as="h1" color="var(--color-text-primary)">
        Unacademy Help & Support
      </MainTitle>
      <SubTitle as="h2" color="var(--color-text-primary)">
        I need more information on:
      </SubTitle>
      <Container>
        {categories.map((category) => (
          <Category key={category.id} onClick={onCategoryClick(category)}>
            <Link
              href="/helpcenter/category/[categoryID]/articles"
              as={`/helpcenter/category/${category.id}/articles`}
              anchorStyle={anchorStyle}
            >
              <Icon src={categoryIdIconMap[category.id]} />
              <H5 color="var(--color-text-primary)">{category.name}</H5>
              <CategoryDesc color="var(--color-text-secondary)">
                {category.description}
              </CategoryDesc>
            </Link>
          </Category>
        ))}
        {!isEducator && showFreshbot && (
          <ChatCategory key="chat">
            <Icon src={categoryIdIconMap.chat} />
            <H5 color="var(--color-text-primary)">Need more assistance?</H5>
            <BoldP1 onClick={openChat}>Chat with us</BoldP1>
          </ChatCategory>
        )}
      </Container>
    </Wrapper>
  );
};

export default Categories;
